import {Component, EventEmitter, Input, Output} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {DropdownOption} from '../../../../../models/dropdown-option.model';
import {CommonService} from '../../../../shared/services/common.service';
import {Store} from '@ngrx/store';
import {debounceTime, filter, Subject, takeUntil} from "rxjs";
import * as Validation from '../../../../shared/constants/validation.constants';
import {
  measurements,
  roles,
  statesList,
  groupStatuses
} from '../../../../shared/constants/common.constants';
import {profile, screenSize} from '../../../../shared/core/shared.selectors';
import {
  ClearAutoGeneratedGroupName,
  ClearClientDetails,
  ClearDuplicateGroup,
  ClearGroupName,
  DeleteGroupRequest,
  FetchClientDetailsRequest,
  FetchClientFilterRequest,
  FetchCoachesFilterRequest,
  FetchGroupStatusFilterRequest,
  FetchMeasurementsFilterRequest,
  GenerateGroupNameRequest,
  UpdateGroupDetailsRequest,
  VerifyGroupNameRequest
} from '../../core/group-details.actions';
import {
  autoGeneratedGroupName,
  clientDetails,
  clientFilter,
  coachesFilter,
  duplicateGroup,
  groupDetails,
  groupStatusFilter,
  measurementsFilter,
  verifyGroupName
} from '../../core/group-details.selector';
import {Filter} from '../../../../../models/filter';
import * as moment from 'moment';
import {GroupDetailsService} from "../../core/group-details.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ClientDetails} from "../../../../../models/client-details";

@Component({
  selector: 'app-group-details-screen',
  templateUrl: './group-details-screen.component.html',
  styleUrls: ['./group-details-screen.component.css']
})
export class GroupDetailsScreenComponent {
  @Input() groupId: string;
  @Input() onPreviousData: any;
  @Output() handleContinueClick = new EventEmitter();
  @Output() handleStartDateChange = new EventEmitter();

  errorMessages: any = {};
  profileData: any;
  clientDetails: any;
  isEditable: boolean = false;
  validation = Validation;
  statesOptions: DropdownOption[] = statesList;
  clientFilterDetails: Filter = {
    list: [],
    total: 0,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  measurementOptions: DropdownOption[] = [];
  coachesOptions: DropdownOption[] = [];
  groupStatusOptions: DropdownOption[] = [];
  isGroupNameAvailable: boolean = false;
  groupNameStatusLoading: boolean = false;
  displayMessage: boolean = false;
  groupDetails: any;
  screenSize = '';
  verifyStatusIcon = null;
  isVerifyDisabled = false;
  generatedGroupName: any;
  displayErrorField: boolean = false;
  isSessionStartDateUpdated: boolean = false;
  skipSessionStartDateChangeListener: boolean = true;
  previousSessionStartDate: string;
  displayAutoGenerateButton: boolean = false;
  minimumDate: any;
  defaultSelectedMeasurement: any;
  coachesOptionsHasNext: boolean = false;
  coachesOptionsPageNumber: number = 1;
  coachOptionsLoading: boolean = false
  selectedClient: any;
  clientId;
  coachTypes = {
    adultCoach: 1,
    childCoach: 2,
    substitute: 3,
  };
  groupStatus = {
    notStarted: 1,
    active: 2,
    completed: 3,
    discontinued: 4
  };
  sessionFormatOptions = [
    {
      key: 1,
      value: 'Group',
    },
    {
      key: 2,
      value: 'Individual family',
    },
  ];
  groupTypes = {online: 1, inPerson: 2, hybrid: 3};
  radioFieldOptionForTypesOfGroup = [
    {
      label: 'In-person',
      value: this.groupTypes.inPerson
    },
    {
      label: 'audio and video',
      value: this.groupTypes.online
    },
    {
      label: 'audio only',
      value: this.groupTypes.hybrid
    }
  ];
  minimumBmiTypes = {per85: 1, per95: 2, notApplicable: 3, other: 4};
  radioFieldOptionForMinimumBmi = [
    {
      label: '85%ile',
      value: this.minimumBmiTypes.per85,
    },
    {
      label: '95%ile',
      value: this.minimumBmiTypes.per95,
    },
    {
      label: 'Not Applicable',
      value: this.minimumBmiTypes.notApplicable,
    },
    {
      label: 'Other',
      value: this.minimumBmiTypes.other,
    },
  ];
  radioFieldOptionForFinancialAidAccepted = [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ];
  errorMessageMap = {
    name: {
      required: 'This field is required',
      minlength: 'Minimum 2 characters required',
      maxlength: 'Maximum 30 characters accepted',
      notVerified: 'Group name not verified',
      pattern: 'Only alphanumeric + symbols(_ - :) allowed'
    },
    clientId: {
      required: 'This field is required'
    },
    clientName: {},
    status: {
      required: 'This field is required'
    },
    type: {
      required: 'This field is required'
    },
    addressLine1: {
      required: 'This field is required.',
      pattern: 'Only alphanumeric + symbols(, _ / - ) allowed',
      minlength: 'Minimum 5 characters required',
      maxlength: 'Maximum 250 characters accepted'
    },
    city: {
      required: 'This field is required.',
      pattern: 'Only alphabets allowed',
      minlength: 'Minimum 2 characters required',
      maxlength: 'Maximum 100 characters accepted'
    },
    state: {
      required: 'Required.'
    },
    zipCode: {
      required: 'Required.',
      minlength: 'Minimum 5 digits required',
      maxlength: 'Maximum 10 digits accepted'
    },
    note: {},
    metrics: {
      required: 'This field is required.'
    },
    adultCoach: {
      required: 'This field is required.'
    },
    childCoach: {
      required: 'This field is required.'
    },
    substituteCoachOne: {
      required: 'This field is required.'
    },
    substituteCoachTwo: {
      required: 'This field is required.'
    },
    substituteCoachThree: {
      required: 'This field is required.'
    },
    substituteCoachFour: {
      required: 'This field is required.'
    },
    sessionStartDateTime: {
      required: 'This field is required.'
    },
    sessionFormat: {
      required: 'This field is required.'
    },
    duration: {
      required: 'Required.'
    },
    minimumBmi: {
      required: 'This field is required.'
    },
    customBmi: {
      required: 'Required',
      minlength: 'Minimum 1 digits required',
      maxlength: 'Maximum 2 digits accepted'
    },
    maxParticipantAge: {
      minlength: 'Minimum 1 digits required',
      maxlength: 'Maximum 2 digits accepted',
      maxFailed: `Max age can not be smaller than min age`
    },
    minParticipantAge: {
      minlength: 'Minimum 1 digits required',
      maxlength: 'Maximum 2 digits accepted'
    },
    fee: {},
    deposit: {},
    financialAidAccepted: {}
  };

  form = new FormGroup<any>({
    sessionStartDateTime: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    sessionFormat: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    duration: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    name: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.group.name.regex),
        Validators.minLength(this.validation.group.name.length.min),
        Validators.maxLength(this.validation.group.name.length.max),
      ]
    }),
    clientId: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    searchClient: new FormControl({value: '', disabled: false}),
    status: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    type: new FormControl({value: this.groupTypes.inPerson, disabled: false}),
    addressLine1: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.address.regex),
        Validators.minLength(this.validation.address.addressLine1.length.min),
        Validators.maxLength(this.validation.address.addressLine1.length.max),
      ]
    }),
    city: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.address.city.length.min),
        Validators.maxLength(this.validation.address.city.length.max),
      ]
    }),
    state: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    zipCode: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.address.zipCode.length.min),
        Validators.maxLength(this.validation.address.zipCode.length.max),
      ]
    }),
    note: new FormControl({value: '', disabled: false}),
    metrics: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    adultCoach: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    childCoach: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    substituteCoachOne: new FormControl({value: null, disabled: false}),
    substituteCoachTwo: new FormControl({value: null, disabled: false}),
    substituteCoachThree: new FormControl({value: null, disabled: false}),
    substituteCoachFour: new FormControl({value: null, disabled: false}),
    minimumBmi: new FormControl({value: null, disabled: false}),
    maxParticipantAge: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.minLength(this.validation.group.participantAge.length.min),
        Validators.maxLength(this.validation.group.participantAge.length.max),
      ]
    }),
    minParticipantAge: new FormControl({value: null, disabled: false}, {
      validators: [
        Validators.minLength(this.validation.group.participantAge.length.min),
        Validators.maxLength(this.validation.group.participantAge.length.max),
      ]
    }),
    fee: new FormControl({value: null, disabled: false}),
    deposit: new FormControl({value: null, disabled: false}),
    financialAidAccepted: new FormControl({value: null, disabled: false})
  }, {
    validators: [
      this.validateGroupName.bind(this),
      this.clearAddressValidations(),
      this.validateMinAndMaxAge.bind(this)
    ]
  });
  groupDetailsLoader: boolean = false;
  duplicateGroupLoader: boolean = false;
  groupFilterLoader: boolean = false;
  measurementFilterLoader: boolean = false;
  previousStatusValue: number;
  skipStatusChangeListener: boolean = false;
  duplicateCalledOnce: boolean = false;
  usingFilteredArray: boolean = false;
  protected readonly roles = roles;
  protected readonly event = event;
  private readonly onDestroy: Subject<any> = new Subject<any>();
  private groupStatusMap = {
    valid: [1, 2, 3, 4],
    notStarted: 1,
    active: 2,
    completed: 3,
    discontinued: 4
  };

  constructor(
    private commonService: CommonService,
    private store: Store,
    private groupDetailsService: GroupDetailsService,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  clearAddressValidations() {
    return this.commonService.clearFormValidationOnlyIf([
      'addressLine1', 'city', 'state', 'zipCode'
    ], () => {
      return this.form?.controls['type'].value === this.groupTypes.online;
    });
  }

  ngOnInit(): void {
    this.store.dispatch(ClearAutoGeneratedGroupName());
    this.store.dispatch(ClearClientDetails());
    this.store.dispatch(FetchGroupStatusFilterRequest());
    this.store.dispatch(FetchMeasurementsFilterRequest());
    this.store.dispatch(ClearGroupName())

    this.minimumDate = moment().subtract(6, 'months');

    if (this.groupId && !this.onPreviousData) {
      this.form.disable();
    } else {
      this.form.enable();
      this.isEditable = false;
    }
    // Fetch profile selector
    this.store.select(profile)
      .pipe(
        takeUntil(this.onDestroy),
        filter(data => !!data)
      )
      .subscribe(data => {
        this.profileData = data;

        if (this.profileData?.role !== roles?.superAdmin) {
          this.form.patchValue({
            clientId: this.profileData.clientId,
            addressLine1: this.profileData?.address?.addressLine1,
            city: this.profileData?.address?.city,
            state: this.profileData?.address?.state,
            zipCode: this.profileData?.address?.zipCode,
          }, {emitEvent: true});
        }

        if (this.profileData.role === roles?.superAdmin) {
          if (!this.groupId) {
            this.fetchClientsFilterList();
          }
        }
      });

    this.form.controls['sessionStartDateTime'].valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data && !this.form.controls['name'].value) {
          this.displayAutoGenerateButton = true;
        }
      });

    if (this.groupId) {
      this.form.controls['sessionStartDateTime'].valueChanges.pipe(takeUntil(this.onDestroy))
        .subscribe((data) => {
          if (this.skipSessionStartDateChangeListener || !this.form.controls['sessionStartDateTime'].dirty) {
            this.skipSessionStartDateChangeListener = false;
            this.previousSessionStartDate = data;
            return;
          }
          this.commonService.openConfirmationDialog({
            data: {
              message: 'Changing the Group Start Date will reset the schedule of all the sessions of this group. Do you want to continue?',
              submitText: 'Yes',
              classList: 'w-[40vw]',
              handleSubmit: () => {
                this.handleStartDateChange.emit(true);
                this.commonService.closeDialog();
                this.isSessionStartDateUpdated = true;
                this.previousSessionStartDate = data;
              },
              cancelText: 'No',
              handleCancel: () => {
                this.handleStartDateChange.emit(false);
                this.commonService.closeDialog();
                this.displayAutoGenerateButton = false;
                this.isSessionStartDateUpdated = false;
                this.form.patchValue({
                  sessionStartDateTime: this.previousSessionStartDate,
                }, {emitEvent: false});
              }
            }
          });

        });

      this.form.controls['status'].valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe((data) => {

          if (!this.form.controls['status'].dirty) {
            this.skipStatusChangeListener = false
            this.previousStatusValue = data
            return;
          }

          if (this.isEditable && data) {
            if (data === this.groupStatusMap.active) {
              const {sessionStartDateTime} = this.form.getRawValue();
              if (!moment(sessionStartDateTime).isSame(moment(), 'date')) {
                this.commonService.openConfirmationDialog({
                  data: {
                    message: 'You are changing the group status to ' +
                             groupStatuses[data] + '.' +
                             ' Continue?',
                    submitText: 'Yes',
                    classList: 'w-[40vw]',
                    handleSubmit: () => {
                      this.commonService.closeDialog();
                    },
                    cancelText: 'No',
                    handleCancel: () => {
                      this.form.patchValue({
                        status: this.previousStatusValue
                      }, {emitEvent: false});
                      this.commonService.closeDialog();
                    }
                  }
                });
              }
            } else {
              this.previousStatusValue = data
            }
          }
        });
    }

    this.store.select(screenSize).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.screenSize = data;
      });

    this.store.select(clientDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: ClientDetails) => {
        if (data?.id) {
          this.form.patchValue({
            addressLine1: data?.address?.addressLine1,
            city: data?.address?.city,
            state: data?.address?.state,
            zipCode: data?.address?.zip,
          }, {emitEvent: true});
          this.fetchClientListWithSelectedClientIds(data?.id)
        }
      });

    this.form.controls['name']?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.groupNameStatusLoading = false;
        this.verifyStatusIcon = null;
        this.isVerifyDisabled = false;
        this.isGroupNameAvailable = null;
        this.displayMessage = false;
        this.displayAutoGenerateButton = false;
      });

    this.form.controls['sessionFormat'].valueChanges.subscribe((sessionFormat) => {
      if (sessionFormat == '1') {
        this.form.controls['duration'].setValue(90);
      } else {
        this.form.controls['duration'].setValue(30);
      }
    });

    this.form.controls['minimumBmi'].valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(selectedBmi => {
        if (selectedBmi == this.minimumBmiTypes.other) {
          this.form.addControl(
            'customBmi',
            new FormControl({value: null, disabled: false}, {
              validators: [
                Validators.required,
                Validators.minLength(this.validation.group.customBmi.length.min),
                Validators.maxLength(this.validation.group.customBmi.length.max),
              ]
            })
          );
        } else {
          this.form.removeControl('customBmi');
        }
        if (this.groupId && !this.isEditable) {
          this.form.controls['customBmi']?.disable();
        }
      });

    this.form.controls['clientId'].valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe((clientId) => {
        if (clientId) {
          this.clientId = clientId
          this.selectedClient = this.clientFilterDetails?.list?.find((item) => item.key === clientId);
          this.coachesOptions = [];
          this.form.controls['adultCoach'].reset();
          this.form.controls['childCoach'].reset();
          this.form.controls['substituteCoachOne'].reset();
          this.form.controls['substituteCoachTwo'].reset();
          this.form.controls['substituteCoachThree'].reset();
          this.form.controls['substituteCoachFour'].reset();

          if (this.profileData?.role === roles.superAdmin) {
            this.store.dispatch(FetchCoachesFilterRequest({payload: {clientId, status: 3}}));
          } else {
            this.store.dispatch(FetchCoachesFilterRequest({payload: {status: 3}}));
          }

          if (this.profileData.role === roles?.superAdmin && !this.groupId) {
            this.store.dispatch(FetchClientDetailsRequest({
              payload: {
                clientId
              }
            }))
          }
        }
      });

    this.form.controls['searchClient'].valueChanges.pipe(debounceTime(1000), takeUntil(this.onDestroy))
      .subscribe(() => {
        this.clientFilterDetails.pageNumber = 1;
        this.clientFilterDetails.list = [];
        if (this.selectedClient) {
          this.clientFilterDetails.list.push(this.selectedClient);
        }
        this.fetchClientsFilterList();
      });


    this.store.select(clientFilter).pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.clientFilterDetails = JSON.parse(JSON.stringify(data));
      });

    this.store.select(groupStatusFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.groupFilterLoader = data?.loading;

        if(this.router.url.includes('create')) {
          this.groupStatusOptions = data?.list.filter((o) => o.key === this.groupStatus.notStarted || o.key === this.groupStatus.active);
        } else {
          this.groupStatusOptions = data?.list
        }


        const selectedGroupStatus = this.groupStatusOptions.find((item) => item.key === this.groupStatus['notStarted']);
        if (!this.groupId) {
          this.form.patchValue({
            status: selectedGroupStatus?.key
          }, {emitEvent: false});
        } else {
          this.checkStatusAndUpdateGroupStatusFilters();
        }
      });

    this.store.select(coachesFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.coachesOptionsHasNext = data.hasNext;
        this.coachesOptionsPageNumber = data.pageNumber;
        this.coachOptionsLoading = data.loading;
        this.coachesOptions = data?.list.map((item) => ({
          ...item,
          key: item.key,
          value: item.value,
          disabled: false
        }));
      });

    const handleCoachSelection = () => {
      const selectedCoaches = this.getSelectedCoaches();
      this.coachesOptions?.find((item) => {
        item.disabled = selectedCoaches?.includes(item.key);
      });
    };

    this.form.controls['adultCoach'].valueChanges.subscribe(handleCoachSelection);

    this.form.controls['childCoach'].valueChanges.subscribe(handleCoachSelection);

    this.form.controls['substituteCoachOne'].valueChanges.subscribe(handleCoachSelection);

    this.form.controls['substituteCoachTwo'].valueChanges.subscribe(handleCoachSelection);

    this.form.controls['substituteCoachThree'].valueChanges.subscribe(handleCoachSelection);

    this.form.controls['substituteCoachFour'].valueChanges.subscribe(handleCoachSelection);

    this.store.select(autoGeneratedGroupName).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.groupNameStatusLoading = data?.loading;
        if (!data?.loading && data?.name) {
          this.generatedGroupName = data?.name;
          this.form.patchValue({
            name: data?.name
          }, {emitEvent: false});
          this.isVerifyDisabled = true;
          this.displayAutoGenerateButton = false;
        }
        this.verifyStatusIcon = null;
        this.displayMessage = false;
      });

    this.store.select(verifyGroupName).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.loading) {
          this.verifyStatusIcon = null;
        } else if (data.isAvailable !== null) {
          if (data?.isAvailable) {
            this.verifyStatusIcon = 'check-circle';
            this.form.controls['name'].setErrors(null);
            delete this.errorMessages.name;
          } else if (data.isAvailable === false) {
            this.verifyStatusIcon = 'exclamation-circle';
          }
          this.isVerifyDisabled = true;
        }
        this.isGroupNameAvailable = data?.isAvailable;
        this.groupNameStatusLoading = data?.loading;
      });

    this.store.select(groupDetails).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (this.groupDetails?.clientId) {
          this.fetchClientListWithSelectedClientIds(data?.details?.clientId);
        }
        this.groupDetailsLoader = data?.loading;

        if (!data || !data.details) {
          return;
        }
        this.groupDetails = data?.details;

        if (this.groupId) {
          this.checkStatusAndUpdateGroupStatusFilters();
          this.minimumDate = moment(this.groupDetails?.startDate);
          let minimumBMIValue;
          switch (this.groupDetails?.eligibilityCriteria?.minimumBmi) {
            case 85:
              minimumBMIValue = this.minimumBmiTypes.per85;
              break;
            case 95:
              minimumBMIValue = this.minimumBmiTypes.per95;
              break;
            case null:
              minimumBMIValue = this.minimumBmiTypes.notApplicable;
              break;
            default:
              if (this.groupDetails?.eligibilityCriteria?.minimumBmi) {
                minimumBMIValue = this.minimumBmiTypes.other;
              }
          }

          this.generatedGroupName = this.groupDetails?.name;
          if (this.groupDetails?.clientId && this.profileData.role === roles.superAdmin) {
            this.clientFilterDetails.list.push({
              key: this.groupDetails?.clientId,
              value: this.groupDetails?.clientName
            });
          }


          const substituteCoaches = this.groupDetails?.behaviorCoaches?.filter((item) => item.coachTypeId == this.coachTypes.substitute) || [];
          this.form.patchValue({
            sessionStartDateTime: this.groupDetails?.startDate,
            sessionFormat: this.groupDetails?.sessionFormat,
            duration: this.groupDetails?.duration,
            name: this.groupDetails?.name,
            clientId: this.groupDetails?.clientId,
            status: this.groupDetails?.status,
            type: this.groupDetails?.location,
            addressLine1: this.groupDetails?.address?.addressLine1,
            city: this.groupDetails?.address?.city,
            state: this.groupDetails?.address?.state,
            zipCode: this.groupDetails?.address?.zipCode,
            note: this.groupDetails?.note,
            metrics: this.groupDetails?.metrics?.map((item) => item.id),
            adultCoach: this.groupDetails?.behaviorCoaches?.find((item) => item.coachTypeId == this.coachTypes.adultCoach)?.id,
            childCoach: this.groupDetails?.behaviorCoaches?.find((item) => item.coachTypeId == this.coachTypes.childCoach)?.id,
            substituteCoachOne: substituteCoaches[0]?.id || '',
            substituteCoachTwo: substituteCoaches[1]?.id || '',
            substituteCoachThree: substituteCoaches[2]?.id || '',
            substituteCoachFour: substituteCoaches[3]?.id || '',
            minimumBmi: minimumBMIValue,
            customBmi: this.groupDetails?.eligibilityCriteria?.minimumBmi,
            maxParticipantAge: this.groupDetails?.eligibilityCriteria?.maxParticipantAge,
            minParticipantAge: this.groupDetails?.eligibilityCriteria?.minParticipantAge,
            fee: this.groupDetails?.costs?.fee,
            deposit: this.groupDetails?.costs?.deposit,
            financialAidAccepted: this.groupDetails?.costs?.financialAidAccepted
          }, {emitEvent: true});
          this.displayAutoGenerateButton = false;
          if (this.groupDetails?.behaviorCoaches.map(res => res.id).length > 0) {
            this.store.dispatch(FetchCoachesFilterRequest({
              payload: {
                ...(this.profileData?.role === roles.superAdmin ? {clientId: this.clientId} : {}),
                shouldInclude: this.groupDetails?.behaviorCoaches.map(res => res.id),
                pageNumber: 1,
                status: 3
              }
            }))
          }
        }
      });

    this.coachesOptionsPageNumber = 0
    setTimeout(() => {
      this.fetchCoachNextPage();
    }, 0)

    this.groupDetailsService.resetForm.pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.form.reset();
        this.store.dispatch(ClearAutoGeneratedGroupName());
      });

    if (this.onPreviousData) {
      this.isSessionStartDateUpdated = true;
      if (this.groupId) {
        this.form.controls['clientId']?.disable();
        this.isEditable = true;
      }
      let minimumBMIValue;
      switch (this.onPreviousData?.eligibilityCriteria?.minimumBmi) {
        case 85:
          minimumBMIValue = this.minimumBmiTypes.per85;
          break;
        case 95:
          minimumBMIValue = this.minimumBmiTypes.per95;
          break;
        case null:
          minimumBMIValue = this.minimumBmiTypes.notApplicable;
          break;
        default:
          if (this.onPreviousData?.eligibilityCriteria?.minimumBmi) {
            minimumBMIValue = this.minimumBmiTypes.other;
          }
      }

      const substituteCoaches = this.onPreviousData?.coaches?.filter((item) => item.type == this.coachTypes.substitute) || [];

      this.form.patchValue({
        sessionStartDateTime: this.onPreviousData?.startDate,
        sessionFormat: this.onPreviousData?.sessionFormat,
        duration: this.onPreviousData?.duration,
        name: this.onPreviousData?.name,
        clientId: this.onPreviousData?.clientId,
        status: this.onPreviousData?.status,
        type: this.onPreviousData?.location,
        addressLine1: this.onPreviousData?.address?.addressLine1,
        city: this.onPreviousData?.address?.city,
        state: this.onPreviousData?.address?.state,
        zipCode: this.onPreviousData?.address?.zipCode,
        note: this.onPreviousData?.note,
        metrics: this.onPreviousData?.metrics?.map((item) => item.id),
        adultCoach: this.onPreviousData?.coaches?.find((item) => item.type == this.coachTypes.adultCoach)?.id,
        childCoach: this.onPreviousData?.coaches?.find((item) => item.type == this.coachTypes.childCoach)?.id,
        substituteCoachOne: substituteCoaches[0]?.id || '',
        substituteCoachTwo: substituteCoaches[1]?.id || '',
        substituteCoachThree: substituteCoaches[2]?.id || '',
        substituteCoachFour: substituteCoaches[3]?.id || '',
        minimumBmi: minimumBMIValue,
        customBmi: this.onPreviousData?.eligibilityCriteria?.minimumBmi,
        maxParticipantAge: this.onPreviousData?.eligibilityCriteria?.maxParticipantAge,
        minParticipantAge: this.onPreviousData?.eligibilityCriteria?.minParticipantAge,
        fee: this.onPreviousData?.costs?.fee,
        deposit: this.onPreviousData?.costs?.deposit,
        financialAidAccepted: this.onPreviousData?.costs?.financialAidAccepted
      }, {emitEvent: true});
    }

    this.store.select(duplicateGroup).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.duplicateGroupLoader = data?.loading;
        if (data.details !== null) {
          this.duplicateCalledOnce = true
          let minimumBMIValue;
          switch (data.details?.eligibilityCriteria?.minimumBmi) {
            case 85:
              minimumBMIValue = this.minimumBmiTypes.per85;
              break;
            case 95:
              minimumBMIValue = this.minimumBmiTypes.per95;
              break;
            case null:
              minimumBMIValue = this.minimumBmiTypes.notApplicable;
              break;
            default:
              minimumBMIValue = this.minimumBmiTypes.other;
          }

          const substituteCoaches = data.details?.behaviorCoaches?.filter((item) => item.coachTypeId == this.coachTypes.substitute) || [];

          this.form.patchValue({
            sessionFormat: data?.details?.sessionFormat,
            duration: data?.details?.duration,
            clientId: data?.details?.clientId,
            status: this.groupStatus.notStarted,
            type: data?.details?.location,
            addressLine1: data?.details?.address?.addressLine1,
            city: data?.details?.address?.city,
            state: data?.details?.address?.state,
            zipCode: data?.details?.address?.zipCode,
            note: data?.details?.note,
            metrics: data?.details?.metrics?.map((item) => item.id),
            adultCoach: data?.details?.behaviorCoaches?.find((item) => item.coachTypeId == this.coachTypes.adultCoach)?.id,
            childCoach: data?.details?.behaviorCoaches?.find((item) => item.coachTypeId == this.coachTypes.childCoach)?.id,
            substituteCoachOne: substituteCoaches[0]?.id || '',
            substituteCoachTwo: substituteCoaches[1]?.id || '',
            substituteCoachThree: substituteCoaches[2]?.id || '',
            substituteCoachFour: substituteCoaches[3]?.id || '',
            minimumBmi: minimumBMIValue,
            customBmi: data?.details?.eligibilityCriteria?.minimumBmi,
            maxParticipantAge: data?.details?.eligibilityCriteria?.maxParticipantAge,
            minParticipantAge: data?.details?.eligibilityCriteria?.minParticipantAge,
            fee: data?.details?.costs?.fee,
            deposit: data?.details?.costs?.deposit,
            financialAidAccepted: data?.details?.costs?.financialAidAccepted
          }, {emitEvent: true});
          this.store.dispatch(ClearDuplicateGroup());
          if (data.details.clientId) {
            this.fetchClientListWithSelectedClientIds(data.details.clientId)
          }
          if (data?.details?.behaviorCoaches.map(res => res.id).length > 0) {
            this.store.dispatch(FetchCoachesFilterRequest({
              payload: {
                ...(this.profileData?.role === roles.superAdmin ? {clientId: this.clientId} : {}),
                shouldInclude: data?.details?.behaviorCoaches.map(res => res.id),
                pageNumber: 1,
                status: 3
              }
            }))
          }
        } else if (!this.duplicateCalledOnce && this.router.url.includes('duplicate')) {
          const urlPrefix = this.router.url.split('/')[1];
          this.router.navigate([`/${urlPrefix}/group/create`]);
        }
      });

    this.store.select(measurementsFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.measurementFilterLoader = data?.loading
        if (data.list.length > 0) {
          const measurementsList = data?.list;
          this.measurementOptions = measurementsList.map((item) => ({
            key: item.id,
            value: item.name,
            disabled: [
              measurements.metricOption.height, measurements.metricOption.weight
            ].includes(item.code)
          }));
          this.defaultSelectedMeasurement = data?.list.filter((i) => i.code == measurements.metricOption.height || i.code == measurements.metricOption.weight)
            .map((v) => v.id);
          if (!this.route.snapshot.paramMap.get('groupId') && !this.router.url.includes('duplicate')) {
            this.form.patchValue({
              metrics: this.defaultSelectedMeasurement
            }, {emitEvent: true});
          }
        }
      });


    this.groupDetailsService
      .disableForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.form.disable();
        this.isEditable = false;
      });

    this.commonService
      .onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        }

        const prevUrlParams = this.commonService.urlParams.getValue();

        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
         this.commonService.urlParams.next(urlParams);
        }
      });
  }

  fetchClientsFilterList() {
    if (this.profileData.role !== roles?.superAdmin) {
      return;
    }
    const payload: any = {
      pageNumber: this.clientFilterDetails?.pageNumber,
      limit: this.clientFilterDetails?.limit,
      onlyActive: true,
      shouldInclude: this.form.value.clientId
    };
    if (this.form.controls?.['searchClient']?.value.trim()) {
      payload.search = this.form.controls['searchClient'].value;
    }
    this.store.dispatch(FetchClientFilterRequest({payload}));
  }

  clientsOnNextPage() {
    this.clientFilterDetails.pageNumber = this.clientFilterDetails.pageNumber + 1;
    this.fetchClientsFilterList();
  }

  fetchClientListWithSelectedClientIds(clientId) {
    if (this.profileData.role !== roles?.superAdmin) {
      return;
    }
    const payload: any = {
      pageNumber: 1,
      limit: this.clientFilterDetails?.limit,
      onlyActive: true,
      shouldInclude: clientId
    };
    this.store.dispatch(FetchClientFilterRequest({payload}));
  }

  onContinue() {
    if (this.form.invalid) {
      if (this.form?.controls?.['name']?.errors) {
        this.displayErrorField = true;
      }
      this.checkForErrors();
      setTimeout(() => {
        $('.form-field.ng-invalid')[0].parentElement.scrollIntoView();
      }, 0);
      return;
    }

    this.displayErrorField = false;

    const {
      name,
      status,
      type,
      note,
      clientId,
      addressLine1,
      city,
      state,
      zipCode,
      metrics,
      adultCoach,
      childCoach,
      substituteCoachOne,
      substituteCoachTwo,
      substituteCoachThree,
      substituteCoachFour,
      sessionStartDateTime,
      sessionFormat,
      duration,
      minimumBmi,
      customBmi,
      minParticipantAge,
      maxParticipantAge,
      fee,
      deposit,
      financialAidAccepted
    } = this.form.getRawValue();

    // calculate minimum BMI
    let minimumBMIValue;
    switch (minimumBmi) {
      case 1:
        minimumBMIValue = 85;
        break;
      case 2:
        minimumBMIValue = 95;
        break;
      case 3:
        minimumBMIValue = null;
        break;
      default:
        if (minimumBmi) {
          minimumBMIValue = +customBmi;
        }
    }

    const coaches = [];
    if (adultCoach) {
      coaches.push({
        id: adultCoach,
        type: this.coachTypes.adultCoach
      });
    }

    if (childCoach) {
      coaches.push({
        id: childCoach,
        type: this.coachTypes.childCoach
      });
    }

    if (substituteCoachOne) {
      coaches.push({
        id: substituteCoachOne,
        type: this.coachTypes.substitute
      });
    }

    if (substituteCoachTwo) {
      coaches.push({
        id: substituteCoachTwo,
        type: this.coachTypes.substitute
      });
    }

    if (substituteCoachThree) {
      coaches.push({
        id: substituteCoachThree,
        type: this.coachTypes.substitute
      });
    }

    if (substituteCoachFour) {
      coaches.push({
        id: substituteCoachFour,
        type: this.coachTypes.substitute
      });
    }

    const payload = {
      name,
      status,
      location: type,
      note,
      clientId,
      metrics,
      coaches,
      startDate: sessionStartDateTime,
      sessionFormat,
      duration,
      eligibilityCriteria: {
        minimumBmi: minimumBMIValue,
        minParticipantAge,
        maxParticipantAge,
      },
      costs: {
        fee,
        deposit,
        financialAidAccepted
      }
    };
    if (type != this.groupTypes.online) {
      payload['address'] = {
        addressLine1,
        city,
        state,
        zipCode: zipCode ? String(zipCode) : zipCode
      };
    }

    if (!note) {
      delete payload.note;
    }
    if (!fee) {
      delete payload.costs.fee;
    }
    if (!deposit) {
      delete payload.costs.deposit;
    }
    if (!minParticipantAge) {
      delete payload.eligibilityCriteria.minParticipantAge;
    }
    if (!maxParticipantAge) {
      delete payload.eligibilityCriteria.maxParticipantAge;
    }

    if (this.profileData.role !== roles.superAdmin) {
      delete payload.clientId;
    }
    this.skipSessionStartDateChangeListener = true;

    this.handleContinueClick.emit(payload);

  }

  save() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      if (this.form?.controls?.['name']?.errors) {
        this.displayErrorField = true;
      }
      this.checkForErrors();
      setTimeout(() => {
        $('.form-field.ng-invalid')[0].parentElement.scrollIntoView();
      }, 0);
      return;
    }

    const {
      name,
      status,
      type,
      note,
      clientId,
      addressLine1,
      city,
      state,
      zipCode,
      metrics,
      adultCoach,
      childCoach,
      substituteCoachOne,
      substituteCoachTwo,
      substituteCoachThree,
      substituteCoachFour,
      sessionStartDateTime,
      sessionFormat,
      duration,
      minimumBmi,
      customBmi,
      minParticipantAge,
      maxParticipantAge,
      fee,
      deposit,
      financialAidAccepted
    } = this.form.getRawValue();

    // calculate minimum BMI
    let minimumBMIValue;
    switch (minimumBmi) {
      case 1:
        minimumBMIValue = 85;
        break;
      case 2:
        minimumBMIValue = 95;
        break;
      case 3:
        minimumBMIValue = null;
        break;
      default:
        if (minimumBmi) {
          minimumBMIValue = +customBmi;
        }
    }
    const coaches = [];
    if (adultCoach) {
      coaches.push({
        id: adultCoach,
        type: this.coachTypes.adultCoach
      });
    }

    if (childCoach) {
      coaches.push({
        id: childCoach,
        type: this.coachTypes.childCoach
      });
    }

    if (substituteCoachOne) {
      coaches.push({
        id: substituteCoachOne,
        type: this.coachTypes.substitute
      });
    }

    if (substituteCoachTwo) {
      coaches.push({
        id: substituteCoachTwo,
        type: this.coachTypes.substitute
      });
    }

    if (substituteCoachThree) {
      coaches.push({
        id: substituteCoachThree,
        type: this.coachTypes.substitute
      });
    }

    if (substituteCoachFour) {
      coaches.push({
        id: substituteCoachFour,
        type: this.coachTypes.substitute
      });
    }

    const payload = {
      name,
      status,
      location: type,
      note,
      clientId,
      address: {
        addressLine1,
        city,
        state,
        zipCode: zipCode ? String(zipCode) : zipCode
      },
      metrics,
      coaches,
      ...(this.isSessionStartDateUpdated ? {startDate: sessionStartDateTime} : {}),
      sessionFormat,
      duration,
      eligibilityCriteria: {
        minimumBmi: minimumBMIValue,
        minParticipantAge,
        maxParticipantAge,
      },
      costs: {
        fee,
        deposit,
        financialAidAccepted
      }
    };

    if (!note) {
      delete payload.note;
    }
    if (!fee) {
      delete payload.costs.fee;
    }
    if (!deposit) {
      delete payload.costs.deposit;
    }
    if (!minParticipantAge) {
      delete payload.eligibilityCriteria.minParticipantAge;
    }
    if (!maxParticipantAge) {
      delete payload.eligibilityCriteria.maxParticipantAge;
    }
    if (!addressLine1 && !state && !city && !zipCode) {
      delete payload.address;
    }

    if (type == this.groupTypes.online) {
      delete payload.address;
    }

    // ACTION DISPATCH
    if (this.profileData.role !== roles.superAdmin) {
      delete payload.clientId;
    }
    this.skipSessionStartDateChangeListener = true;

    if (status === this.groupStatusMap.completed || status === this.groupStatusMap.discontinued) {
      this.commonService.openConfirmationDialog({
        data: {
          message: 'You are changing the group status to ' +
                    groupStatuses[status] + '.' + ' Continue?',
          submitText: 'Yes',
          classList: 'w-[40vw]',
          handleSubmit: () => {
            if (this.groupId) {
              this.store.dispatch(
                UpdateGroupDetailsRequest({
                  payload: {...payload, groupId: this.groupId},
                }),
              );
            }
            this.commonService.closeDialog();
          },
          cancelText: 'No',
          handleCancel: () => {
            this.commonService.closeDialog();
          }
        }
      })
    } else {
      if (this.groupId) {
        this.store.dispatch(
          UpdateGroupDetailsRequest({
            payload: {...payload, groupId: this.groupId},
          }),
        );
      }
    }
  }

  edit() {
    this.isEditable = true;
    this.form.enable({emitEvent: false});
    this.form.controls['clientId']?.disable({emitEvent: false});
  }

  autoGenerateGroupName() {
    const date = this.form.controls['sessionStartDateTime'].getRawValue();
    const payload = {
      startDate: moment(date).toISOString(true),
    };
    this.store.dispatch(GenerateGroupNameRequest({payload}));
  }

  verifyGroupName() {
    if (this.form?.controls?.['name']?.errors?.['pattern']) {
      this.form.controls['name'].markAsTouched();
      this.errorMessages['name'] = this.errorMessageMap['name'].pattern;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['name']?.errors?.['minlength']) {
      this.form.controls['name'].markAsTouched();
      this.errorMessages['name'] = this.errorMessageMap['name'].minlength;
      this.displayErrorField = true;
      return;
    }

    this.displayErrorField = false;
    const name = this.form.controls['name'].getRawValue();
    this.displayMessage = !!name.length;
    const payload = {
      ...(this.groupDetails?.id ? {id: this.groupDetails.id} : {}),
      name
    };
    this.store.dispatch(VerifyGroupNameRequest({payload}));
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  getSelectedCoaches() {
    let selectedCoaches = [];
    if (this.form.controls['adultCoach'].value) {
      selectedCoaches.push(this.form.controls['adultCoach'].value);
    }
    if (this.form.controls['childCoach'].value) {
      selectedCoaches.push(this.form.controls['childCoach'].value);
    }
    if (this.form.controls['substituteCoachOne'].value) {
      selectedCoaches.push(this.form.controls['substituteCoachOne'].value);
    }
    if (this.form.controls['substituteCoachTwo'].value) {
      selectedCoaches.push(this.form.controls['substituteCoachTwo'].value);
    }
    if (this.form.controls['substituteCoachThree'].value) {
      selectedCoaches.push(this.form.controls['substituteCoachThree'].value);
    }
    if (this.form.controls['substituteCoachFour'].value) {
      selectedCoaches.push(this.form.controls['substituteCoachFour'].value);
    }
    return selectedCoaches;
  }

  validateGroupName(control: AbstractControl): ValidationErrors | null {
    const groupName = control.get('name');
    if (!this.isGroupNameAvailable && groupName && groupName.dirty && this.generatedGroupName != groupName.value) {
      control.get('name').setErrors({
        ...(control.get('name').errors ? control.get('name').errors : {}),
        notVerified: true
      });
    }
    return null;
  }

  onClearSelection(data) {
    if (data) {
      this.coachesOptions = [];
      this.form.controls['adultCoach']?.reset();
      this.form.controls['childCoach']?.reset();
      this.form.controls['substituteCoachOne']?.reset();
      this.form.controls['substituteCoachTwo']?.reset();
      this.form.controls['substituteCoachThree']?.reset();
      this.form.controls['substituteCoachFour']?.reset();
    }
  }

  fetchCoachNextPage() {
    let selectedCoaches: any = {}
    const shouldIncludedCoaches: any[] = []
    selectedCoaches.adultCoach = this.form.controls['adultCoach']?.value;
    selectedCoaches.childCoach = this.form.controls['childCoach']?.value;
    selectedCoaches.substituteCoachOne = this.form.controls['substituteCoachOne']?.value;
    selectedCoaches.substituteCoachTwo = this.form.controls['substituteCoachTwo']?.value;
    selectedCoaches.substituteCoachThree = this.form.controls['substituteCoachThree']?.value;
    selectedCoaches.substituteCoachFour = this.form.controls['substituteCoachFour']?.value;

    Object.keys(selectedCoaches).map(coaches => {
      if (selectedCoaches[coaches]) {
        shouldIncludedCoaches.push(selectedCoaches[coaches])
      }
    });

    this.store.dispatch(FetchCoachesFilterRequest({
      payload: {
        ...(this.profileData?.role === roles.superAdmin ? {clientId: this.clientId} : {}),
        shouldInclude: shouldIncludedCoaches,
        pageNumber: this.coachesOptionsPageNumber + 1,
        status: 3
      }
    }))
  }

  validateMinAndMaxAge(control: AbstractControl): ValidationErrors | null {
    const maxParticipantAge = control.get('maxParticipantAge');
    const minParticipantAge = control.get('minParticipantAge');
    if (maxParticipantAge.value && minParticipantAge.value) {
      if (+maxParticipantAge.value < +minParticipantAge.value) {
        maxParticipantAge.setErrors({maxFailed: true});
      } else {
        maxParticipantAge.setErrors(null);
        this.errorMessages.maxParticipantAge = null;
      }
    } else {
      maxParticipantAge.setErrors(null);
      this.errorMessages.maxParticipantAge = null
    }
    return null;
  }

  checkStatusAndUpdateGroupStatusFilters() {
    if (this.groupDetails && [
      this.groupStatusMap.completed, this.groupStatusMap.discontinued
    ].includes(this.groupDetails.status)) {
      // this.groupStatusOptions = this.groupStatusOptions.filter(item => ![
      //   this.groupStatusMap.active, this.groupStatusMap.notStarted
      // ].includes(+item.key));
    }
  }

  deleteUser() {
    this.commonService.openConfirmationDialog({
      data: {
        message: 'Are you sure you want to continue with delete?',
        submitText: 'Yes',
        classList: 'w-[40vw]',
        handleSubmit: () => {
          let payload = {
            ...(this.profileData?.role === roles.superAdmin ? {clientId: this.clientId} : {}),
            groupId: this.groupId,
            isActive: false
          }
          this.store.dispatch(DeleteGroupRequest({payload}));
        },
        cancelText: 'No',
        handleCancel: () => {
          this.commonService.closeDialog();
        }
      }
    });
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
